import { useMutation } from '@tanstack/react-query';

import useCurrentEmployee from '@frontend/AppSettings/hooks/useCurrentEmployee';
import { PersonDayService } from '@frontend/AppSettings/services/PipelineProjectService';
import { useRuntimeConfig, useRuntimeConfigStatus } from '@frontend/__settings__/runtimeConfig';

/**
 * Tenant defaults — the settings P6's classifier reads, surfaced where an admin can check them.
 *
 * `workweek_start_day` IS NOW WRITABLE (P7a, worklist W7), and the reason the door opened is
 * specific. P6 shipped this panel read-only with a named argument — a tenant-settings mutation is
 * its own surface with its own gating and audit questions, and inventing one there would have
 * been scope that phase never sanctioned (D31). P7a is where it comes due: this column is the
 * origin of every weekly overtime window P7b computes, it DEFAULTS TO MONDAY, and the pilot's
 * week starts SUNDAY. An unset default is not neutral here; it silently shifts which days fall in
 * which week, and the first symptom is a plausible overtime figure that is a day out.
 *
 * `default_jurisdiction` stays read-only. It has no write endpoint and no argument for one yet —
 * only the column P7b actually reads got a door.
 *
 * THE GATE IS permish {1}, matching `PUT /api/config/workweek-start-day` exactly. Gating on the
 * nearest client-side approximation is the `ProjectSitesTab` bug this programme has now written
 * down three times: a caller who sees the control must be a caller the server would accept.
 *
 * Why surface it at all before P7 classifies anything: these two values silently decide which
 * seven days a weekly overtime threshold counts and which law a new matter defaults to. An admin
 * who cannot see them cannot check them, and the first symptom of a wrong workweek start is a
 * plausible-looking overtime figure that is a day out.
 */

/**
 * 0037 stores the day as SMALLINT 0-6 with SUNDAY AS ZERO — which is `Date.getDay()`'s order, and
 * the only reading under which the column's `DEFAULT 1` means Monday rather than Tuesday. The
 * backend pins the same convention in `period_builder.week_start`; if these two ever disagree,
 * the screen and the classifier are describing different weeks.
 */
const DAY_NAMES = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];

const TenantDefaultsPanel = (): JSX.Element => {
  const config = useRuntimeConfig();
  const status = useRuntimeConfigStatus();

  const currentEmployee = useCurrentEmployee();
  const rawPermish = currentEmployee?.permish_id ?? null;
  const permish = typeof rawPermish === 'string' ? parseInt(rawPermish, 10) : rawPermish;
  // The SERVER's predicate, not the nearest approximation — see the header comment.
  const mayManage = permish === 1;

  const setWorkweek = useMutation({
    mutationFn: (day: number) => PersonDayService.setWorkweekStartDay(day),
    onSuccess: () => {
      // A full reload, and it is the only mechanism available rather than a shortcut. Runtime
      // config is not a React Query resource — it is a `useSyncExternalStore` over a module
      // singleton whose loader is one-shot (`loadStarted`), so there is no in-place refresh to
      // call. An earlier version invalidated a `['RuntimeConfig']` key that nothing registers,
      // which refreshed nothing and left the reload doing the work unannounced. The panel holds
      // no unsaved state of its own, and the tab switch has already discarded the catalog form's.
      window.location.reload();
    },
  });

  // EVERY hook runs before the early returns below. They sat after them until the dev server's
  // eslint caught it: a component that returns early on a pending config would call a different
  // number of hooks on the next render, which is the rule React states and the bug class it
  // states it for. The unit tests never saw it, because they render with the config ready.
  if (status === 'pending') {
    return <p className="bid-admin-empty">Loading tenant defaults…</p>;
  }
  if (status === 'unavailable' || !config) {
    return (
      <p className="bid-admin-empty">
        Tenant configuration could not be loaded, so these defaults cannot be shown.
      </p>
    );
  }

  const { workweekStartDay, jurisdiction, currency } = config.defaults;
  const dayName = DAY_NAMES[workweekStartDay] ?? `Unrecognised (${workweekStartDay})`;

  return (
    <div className="bid-admin-defaults">
      <table>
        <tbody>
          <tr>
            <th scope="row">Workweek starts</th>
            <td>
              {mayManage ? (
                <select
                  aria-label="Workweek starts"
                  value={String(workweekStartDay ?? 1)}
                  disabled={setWorkweek.isPending}
                  onChange={(e) => setWorkweek.mutate(Number(e.target.value))}
                >
                  {DAY_NAMES.map((name, index) => (
                    <option key={name} value={index}>{name}</option>
                  ))}
                </select>
              ) : dayName}
            </td>
            <td className="bid-admin-hint">
              Which seven days a weekly overtime threshold counts. Changing it moves every weekly
              total by a day.
            </td>
          </tr>
          <tr>
            <th scope="row">Default jurisdiction</th>
            <td>{jurisdiction ?? 'Not set'}</td>
            <td className="bid-admin-hint">
              The employment standards a new matter starts from. A site can differ — the site is
              what actually picks the law for hours booked at it.
            </td>
          </tr>
          <tr>
            <th scope="row">Currency</th>
            <td>{currency}</td>
            <td className="bid-admin-hint">What money resolved for this tenant is denominated in.</td>
          </tr>
        </tbody>
      </table>
      <p className="bid-admin-empty">
        {mayManage
          ? 'The workweek start is editable here. The other two live on the tenant record and are '
            + 'set at provisioning; ask your SEPT contact to change one.'
          : 'These are read-only here. They live on the tenant record and are set at provisioning; '
            + 'ask your SEPT contact to change one.'}
      </p>
      {setWorkweek.isError && (
        <p className="bid-admin-error" role="alert">
          {(setWorkweek.error as Error)?.message || 'Could not change the workweek start.'}
        </p>
      )}
    </div>
  );
};

export default TenantDefaultsPanel;
